import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch } from '@app/hooks';
import {
  apiErrorText,
  osnovaApi,
  useCreateFieldElementsFromRdMutation,
  useGetFieldSiteRdPositionsQuery,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { rdPositionChoices, rdPositionChoiceLabel, rdPositionChoiceNotes } from '@entities/field';

const Group = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;
const Picker = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 10px;
`;
const Choices = styled.div`
  max-height: 260px;
  overflow: auto;
`;
const Choice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
  margin: 8px 0;
`;
const Note = styled.span`
  font-size: 13px;
`;
const SubNote = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Батчи ограничены сервером. Остановка не отменяет уже записанную часть. */
export function FieldRdImportButton({
  siteId,
  titulId,
  disabled = false,
}: {
  siteId: string;
  titulId: string | null;
  disabled?: boolean;
}) {
  const dispatch = useAppDispatch();
  const [fromRd] = useCreateFieldElementsFromRdMutation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [allTitul, setAllTitul] = useState(false);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const positionsQ = useGetFieldSiteRdPositionsQuery(siteId, { skip: !open || !titulId });
  const allChoices = rdPositionChoices(positionsQ.currentData?.positions ?? []).filter(
    (c) => (c.position.quantity ?? 0) > 0,
  );
  const verifiedCount = allChoices.filter((c) => c.decision === 'accepted').length;
  const choices = onlyVerified
    ? allChoices.filter((c) => c.decision === 'accepted')
    : allChoices;
  const filtered = choices.filter((c) =>
    rdPositionChoiceLabel(c).toLowerCase().includes(search.toLowerCase()),
  );
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [nextOffset, setNextOffset] = useState(0);
  const [error, setError] = useState('');
  const stop = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stop.current = true;
    };
  }, []);
  async function run() {
    const targetIds = allTitul
      ? onlyVerified
        ? choices.map((c) => c.position.id)
        : undefined
      : selected;
    if (
      !titulId ||
      disabled ||
      running ||
      (!allTitul && selected.length === 0) ||
      (allTitul && onlyVerified && choices.length === 0)
    )
      return;
    if (
      !window.confirm(
        'Добавить конструктив только из РД выбранного титула? Работы выполняются порциями, уже добавленные позиции не дублируются. Для разных фронтов одного титула инженер должен проверить границы работ.',
      )
    )
      return;
    stop.current = false;
    setRunning(true);
    setError('');
    let offset = nextOffset;
    let created = 0;
    let skipped = 0;
    try {
      for (;;) {
        const result = await fromRd({
          siteId,
          offset,
          limit: 100,
          position_ids: targetIds,
        }).unwrap();
        offset = result.offset + result.processed;
        created += result.created.length;
        skipped += result.skipped;
        if (mounted.current) {
          setNextOffset(result.has_more ? offset : 0);
          setProgress(
            `Обработано ${Math.min(offset, result.total)} из ${result.total}; добавлено ${created}, уже было ${skipped}.`,
          );
        }
        if (!result.has_more || stop.current) break;
        if (result.processed <= 0)
          throw new Error('Сервер не продвинул обработку. Повторите позже.');
      }
    } catch (e) {
      if (mounted.current) {
        setNextOffset(offset);
        setError(
          apiErrorText(
            e,
            'Обработка остановлена. Повторите — ранее добавленные элементы сохранятся.',
          ),
        );
      }
    } finally {
      dispatch(osnovaApi.util.invalidateTags(['Field']));
      if (mounted.current) setRunning(false);
    }
  }
  return (
    <Group>
      <Button
        $variant="secondary"
        $size="sm"
        disabled={disabled || !titulId || running}
        onClick={() => setOpen((v) => !v)}
        title={
          !titulId
            ? 'Сначала распределите площадку по титулу'
            : 'Конструктив только из явно назначенной титулу РД'
        }
      >
        {running
          ? 'Добавляю конструктив…'
          : nextOffset
            ? 'Продолжить конструктив из РД'
            : 'Конструктив из РД титула'}
      </Button>
      {open && (
        <Picker>
          <Note>
            Выберите только позиции данного фронта. Для двух фронтов одного титула распределите
            работу по осям / участкам; весь титул на каждый фронт автоматически не копируется.
          </Note>
          <Choice>
            <input
              type="checkbox"
              checked={onlyVerified}
              disabled={running}
              onChange={(e) => {
                setOnlyVerified(e.target.checked);
                setPage(0);
                setNextOffset(0);
              }}
            />
            Только проверенные ПТО позиции ({verifiedCount} из {allChoices.length})
          </Choice>
          <Choice>
            <input
              type="checkbox"
              checked={allTitul}
              disabled={running}
              onChange={(e) => {
                setAllTitul(e.target.checked);
                setNextOffset(0);
              }}
            />
            Весь титул относится к этому фронту — добавить все позиции
          </Choice>
          {!allTitul && (
            <>
              <Input
                aria-label={`Поиск конструктива ${siteId}`}
                value={search}
                disabled={running}
                placeholder="Шифр, работа, марка или оси"
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
              <Choices>
                {filtered.slice(page * 25, (page + 1) * 25).map((c) => {
                  const notes = rdPositionChoiceNotes(c);
                  return (
                    <Choice key={c.position.id}>
                      <input
                        type="checkbox"
                        disabled={running}
                        checked={selected.includes(c.position.id)}
                        onChange={(e) => {
                          setSelected((prev) =>
                            e.target.checked
                              ? [...prev, c.position.id]
                              : prev.filter((id) => id !== c.position.id),
                          );
                          setNextOffset(0);
                        }}
                      />
                      <div>
                        <div>{rdPositionChoiceLabel(c)}</div>
                        {notes.length > 0 && <SubNote>{notes.join(' · ')}</SubNote>}
                      </div>
                    </Choice>
                  );
                })}
              </Choices>
              <Group>
                <Button
                  $variant="ghost"
                  $size="sm"
                  disabled={running || page === 0}
                  onClick={() => setPage((n) => n - 1)}
                >
                  Назад
                </Button>
                <Note>
                  {filtered.length
                    ? `${page * 25 + 1}–${Math.min((page + 1) * 25, filtered.length)} из ${filtered.length}`
                    : 'Подходящих позиций нет'}
                </Note>
                <Button
                  $variant="ghost"
                  $size="sm"
                  disabled={running || (page + 1) * 25 >= filtered.length}
                  onClick={() => setPage((n) => n + 1)}
                >
                  Далее
                </Button>
              </Group>
            </>
          )}
          {positionsQ.isFetching && <Note>Загрузка позиций титула…</Note>}
          {positionsQ.error && (
            <Note role="alert">
              {apiErrorText(positionsQ.error, 'Не удалось загрузить позиции титула.')}
            </Note>
          )}
          <Button
            $size="sm"
            disabled={
              running ||
              positionsQ.isFetching ||
              !!positionsQ.error ||
              (!allTitul && selected.length === 0) ||
              (allTitul && onlyVerified && choices.length === 0)
            }
            onClick={() => void run()}
          >
            {nextOffset
              ? 'Продолжить добавление'
              : allTitul
                ? onlyVerified
                  ? `Добавить проверенные позиции титула (${choices.length})`
                  : 'Добавить весь титул'
                : `Добавить выбранные позиции (${selected.length})`}
          </Button>
        </Picker>
      )}
      {running && (
        <Button
          $variant="ghost"
          $size="sm"
          onClick={() => {
            stop.current = true;
            setProgress((p) => `${p} Останавливаю после текущей порции…`);
          }}
        >
          Остановить
        </Button>
      )}
      {progress && <Note role="status">{progress}</Note>}
      {error && <Note role="alert">{error}</Note>}
    </Group>
  );
}
